import { z } from 'zod';
import type { SchoolProfile } from './schools';

// ---------------------------------------------------------------- comercial
export const PlanSchema = z.enum(['ESSENCIAL', 'PROFISSIONAL', 'REDE']);
export type Plan = z.infer<typeof PlanSchema>;
export const PLAN_LABELS: Record<Plan, string> = { ESSENCIAL: 'Essencial', PROFISSIONAL: 'Profissional', REDE: 'Rede' };

export const SubscriptionStatusSchema = z.enum(['ACTIVE', 'TRIALING', 'PAST_DUE', 'CANCELED']);
export type SubscriptionStatus = z.infer<typeof SubscriptionStatusSchema>;
export const SUBSCRIPTION_STATUS_LABELS: Record<SubscriptionStatus, string> = {
  ACTIVE: 'Ativa',
  TRIALING: 'Em teste',
  PAST_DUE: 'Inadimplente',
  CANCELED: 'Cancelada',
};

export const ContractStatusSchema = z.enum(['ACTIVE', 'PENDING_SIGNATURE', 'EXPIRED', 'CANCELLED']);
export type ContractStatus = z.infer<typeof ContractStatusSchema>;
export const CONTRACT_STATUS_LABELS: Record<ContractStatus, string> = {
  ACTIVE: 'Vigente',
  PENDING_SIGNATURE: 'Aguardando assinatura',
  EXPIRED: 'Expirado',
  CANCELLED: 'Cancelado',
};

export const PlatformKeySchema = z.enum(['GESTAO', 'ESTUDANTES', 'RESPONSAVEIS']);
export type PlatformKey = z.infer<typeof PlatformKeySchema>;
export const PLATFORM_LABELS: Record<PlatformKey, string> = {
  GESTAO: 'TaskAll Gestão',
  ESTUDANTES: 'TaskAll Estudantes',
  RESPONSAVEIS: 'TaskAll Responsáveis',
};

// ---------------------------------------------------------------- analytics
const Count = z.number().int().nonnegative();
const Money = z.number(); // centavos (BRL)

export const PlatformAnalyticsSchema = z.object({
  generatedAt: z.string(),
  totals: z.object({
    schools: Count,
    activeSchools: Count,
    students: Count,
    activeStudents: Count,
    teachers: Count,
    classes: Count,
    schoolUsers: Count,
  }),
  subscriptions: z.object({
    total: Count,
    byStatus: z.record(SubscriptionStatusSchema, Count),
    mrrCents: Money,
    arrCents: Money,
    averageTicketCents: Money.nullable(),
    churnRate90d: z.number().nullable(),
    byPlan: z.array(z.object({ plan: PlanSchema, count: Count, mrrCents: Money })),
  }),
  contracts: z.object({
    total: Count,
    byStatus: z.record(ContractStatusSchema, Count),
    activeMonthlyValueCents: Money,
    contractedSeats: Count,
    usedSeats: Count,
    seatUtilization: z.number().nullable(),
    expiringSoon: z.array(
      z.object({
        id: z.string(),
        number: z.string(),
        customerName: z.string(),
        plan: PlanSchema,
        endDate: z.string(),
        daysLeft: z.number().int(),
        monthlyValueCents: Money,
      }),
    ),
  }),
  growth: z.array(z.object({ month: z.string(), newSchools: Count, newStudents: Count })),
  schools: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      code: z.string(),
      administrativeSector: z.enum(['PUBLIC', 'PRIVATE']),
      status: z.enum(['ACTIVE', 'INACTIVE']),
      activeStudents: Count,
      plan: PlanSchema.nullable(),
      subscriptionStatus: SubscriptionStatusSchema.nullable(),
      mrrCents: Money,
      createdAt: z.string(),
    }),
  ),
});
export type PlatformAnalytics = z.infer<typeof PlatformAnalyticsSchema>;

// ---------------------------------------------------------------- saúde
export const HealthLevelSchema = z.enum(['OPERATIONAL', 'DEGRADED', 'DOWN', 'UNKNOWN']);
export type HealthLevel = z.infer<typeof HealthLevelSchema>;
export const HEALTH_LEVEL_LABELS: Record<HealthLevel, string> = {
  OPERATIONAL: 'Operacional',
  DEGRADED: 'Degradado',
  DOWN: 'Indisponível',
  UNKNOWN: 'Sem dados',
};

export const LogSeveritySchema = z.enum(['DEBUG', 'INFO', 'NOTICE', 'WARNING', 'ERROR', 'CRITICAL', 'ALERT', 'EMERGENCY', 'DEFAULT']);
export type LogSeverity = z.infer<typeof LogSeveritySchema>;

/** Resultado de cada fonte de dados — falhas são reportadas, nunca quebram a tela. */
export const SourceStatusSchema = z.object({
  source: z.enum(['CLOUD_LOGGING', 'FIRESTORE', 'AUTH', 'UPTIME']),
  ok: z.boolean(),
  message: z.string().nullable(),
});

export const PlatformLogEntrySchema = z.object({
  id: z.string(),
  platform: PlatformKeySchema,
  timestamp: z.string(),
  severity: LogSeveritySchema,
  service: z.string().nullable(),
  message: z.string(),
});
export type PlatformLogEntry = z.infer<typeof PlatformLogEntrySchema>;

export const PlatformHealthItemSchema = z.object({
  key: PlatformKeySchema,
  name: z.string(),
  projectId: z.string(),
  url: z.string().nullable(),
  level: HealthLevelSchema,
  /** Motivo legível do nível (null quando operacional). */
  levelReason: z.string().nullable(),
  uptime: z.object({ ok: z.boolean(), statusCode: z.number().int().nullable(), latencyMs: z.number().nullable(), checkedAt: z.string() }).nullable(),
  errors24h: Count.nullable(),
  warnings24h: Count.nullable(),
  /** true quando a contagem atingiu o limite de leitura (exibir como "N+"). */
  countsCapped: z.boolean(),
  authUsers: Count.nullable(),
  collections: z.array(z.object({ label: z.string(), path: z.string(), count: Count.nullable() })),
  services: z.array(z.object({ name: z.string(), errors24h: Count, warnings24h: Count, lastErrorAt: z.string().nullable() })),
  sources: z.array(SourceStatusSchema),
});
export type PlatformHealthItem = z.infer<typeof PlatformHealthItemSchema>;

export const PlatformHealthSchema = z.object({
  generatedAt: z.string(),
  mode: z.enum(['LIVE', 'DEMO']),
  windowHours: z.number().int(),
  platforms: z.array(PlatformHealthItemSchema),
  logs: z.array(PlatformLogEntrySchema),
});
export type PlatformHealth = z.infer<typeof PlatformHealthSchema>;

export const PlatformLogsQuerySchema = z.object({
  platform: PlatformKeySchema.optional(),
  severity: z.enum(['WARNING', 'ERROR']).optional(),
  hours: z.coerce.number().int().min(1).max(168).default(24),
});
export type PlatformLogsQuery = z.input<typeof PlatformLogsQuerySchema>;

// ---------------------------------------------------------------- INEP
export const InepLookupStatusSchema = z.enum(['FOUND', 'NOT_FOUND', 'UNAVAILABLE']);
export type InepLookupStatus = z.infer<typeof InepLookupStatusSchema>;

/** Campo textual do catálogo: ausente em importações antigas → null. */
const catalogText = z.string().nullable().default(null);

export const InepSchoolSchema = z.object({
  inepCode: z.string(),
  name: z.string(),
  uf: catalogText,
  city: catalogText,
  /** Endereço como publicado pelo INEP (inclui CEP, município e UF). */
  address: catalogText,
  cep: catalogText,
  phone: catalogText,
  /** Categoria Administrativa: "Pública" / "Privada". */
  administrativeCategory: catalogText,
  /** Derivado da categoria/dependência. */
  administrativeSector: z.enum(['PUBLIC', 'PRIVATE']).nullable().default(null),
  /** Federal, Estadual, Municipal ou Privada. */
  dependency: catalogText,
  /** Urbana / Rural. */
  location: catalogText,
  differentiatedLocation: catalogText,
  privateCategory: catalogText,
  publicAgreement: catalogText,
  regulation: catalogText,
  size: catalogText,
  /** Restrição de atendimento / situação de funcionamento. */
  situation: catalogText,
  stages: z.array(z.string()).default([]),
  educationLevels: z.array(z.enum(['EARLY_CHILDHOOD', 'ELEMENTARY_1', 'ELEMENTARY_2', 'HIGH_SCHOOL'])).default([]),
});
export type InepSchool = z.output<typeof InepSchoolSchema>;

/** Converte o registro do INEP no perfil da escola (campos do cadastro). */
export function inepToProfile(school: InepSchool): SchoolProfile {
  return {
    uf: school.uf,
    city: school.city,
    cep: school.cep,
    phone: school.phone,
    location: school.location,
    differentiatedLocation: school.differentiatedLocation,
    dependency: school.dependency,
    privateCategory: school.privateCategory,
    publicAgreement: school.publicAgreement,
    regulation: school.regulation,
    size: school.size,
    stages: school.stages,
    situation: school.situation,
  };
}

export const InepLookupSchema = z.object({
  status: InepLookupStatusSchema,
  source: z.enum(['INEP_CATALOG', 'SIMCAQ', 'NONE']),
  school: InepSchoolSchema.nullable(),
  /** Já existe escola cadastrada na TaskAll com este INEP. */
  alreadyRegistered: z.boolean(),
  message: z.string().nullable(),
});
export type InepLookup = z.infer<typeof InepLookupSchema>;
